import logos from '@/content/logos.json'

/**
 * One entry in the technical trust bar — a chip, whether or not we hold a mark.
 *
 * `SiteLogo` (the customer wall) assumes most marks exist and a few are missing,
 * so a missing one renders as bare text among logos and reads fine. This section
 * shipped as the reverse — sixteen names and not one sourced file — which is why
 * the chip exists: it makes "SAP" and a rendered mark read as the same kind of
 * thing, a slot in a list filled or not, rather than making the text look like a
 * formatting failure.
 *
 * Twelve of the fifteen landed on 2026-08-25 and the last three (IFS, DeepSeek,
 * Qwen) on 2026-08-28, so **every company in the bar now has a mark** and the
 * only text left is "On Premise" and "and many more", neither of which is a
 * company. The chip's pill went with that change — *"bu logolar çerçevesiz
 * olsun"* — because a border round a real mark reads as a button, and it was
 * only ever there to stop a bare company name looking like lost formatting.
 * The fallback branch stays: it is what a name renders as until its file lands,
 * and it is still what the two non-companies use.
 *
 * Drop a file at `/logo/<slug>.webp` (via `logo-src/`, the same pipeline the
 * customer wall uses — see `pnpm images`) and the chip upgrades itself; nothing
 * here or in `src/content/stack.ts` has to change.
 */
type Manifest = Record<string, { width: number; height: number; webp: { w: number; src: string }[]; fallback: string }>

export default function StackBadge({
  item,
}: {
  item: { name: string; src: string; padded?: boolean }
}) {
  const entry = (logos as Manifest)[item.src]

  // Equal height is not equal weight — the same argument `SiteLogo` makes about
  // the customer wall, and the same buckets, because it is the same problem one
  // row down the page. One height for every mark renders Anthropic's 7:1
  // wordmark 144px across and Qwen's square badge 36px across, and the reader
  // sees one brand shouting and one whispering. Asked for on 2026-08-28 —
  // *"logolar kendi arasında uyumlu tutarlı ve bütünlüklü olsun"* — naming MAI
  // and Salesforce, which are the two smallest of the sixteen.
  //
  // So the cap follows the shape: a wide mark is limited by height (and, past
  // about 4:1, by `max-w` before that), a near-square one gets a taller
  // allowance, and both end up occupying a similar amount of ink. Nothing gets
  // smaller than it was — the widest bucket keeps the `h-9` every mark used to
  // share, and only the marks that were losing grow.
  const aspect = entry ? entry.width / entry.height : 1

  // A padded file is the one case where capping by height is the wrong answer.
  // The MAI mark is a wordmark floating in the middle of a square canvas, so
  // `h-9` drew about 15px of actual ink; sizing the *canvas* at the full row
  // puts its ink where its neighbours' ink is. See the note in `content/stack.ts`.
  //
  // Every bucket came down a step on 2026-09-15 ("biraz daha küçük"), keeping
  // the ratios between them intact — the balancing above is about the buckets
  // relative to each other, so scaling the whole set preserves it.
  const size = item.padded
    ? 'h-14'
    : aspect >= 3
      ? 'h-7'
      : aspect >= 1.7
        ? 'h-9'
        : 'h-11'

  return (
    // `shrink-0` and `whitespace-nowrap`: on the wide layout the row is
    // `flex-nowrap`, and without these a chip would be squeezed thin and break
    // "Google Cloud" across two lines. The fixed row height stays after the
    // border went, so every slot still occupies one row height and the marks sit
    // on a shared centre line whatever their own proportions — it tracks the
    // tallest mark, which is now the padded one, keeping a little air above and
    // below.
    <li className="flex h-14 shrink-0 items-center whitespace-nowrap">
      {entry ? (
        // `max-w-[112px]` catches the extreme wordmarks, which would otherwise
        // be twice their neighbours' width — Anthropic at 7:1 would run 196px
        // across at `h-7`. It moves with the height buckets and only with them,
        // since it is the ceiling the widest bucket is measured against.
        <img
          src={entry.fallback}
          srcSet={entry.webp.map((v) => `${v.src} ${v.w}w`).join(', ')}
          sizes="112px"
          width={entry.width}
          height={entry.height}
          alt={item.name}
          loading="lazy"
          decoding="async"
          className={`${size} w-auto max-w-[112px] object-contain`}
        />
      ) : (
        <span className="text-sm font-medium text-foreground">{item.name}</span>
      )}
    </li>
  )
}
